/**
 * LoveLab necklace launch takeover — React NecklaceTakeover parity.
 * Shown once per browser session on the homepage. Dismiss via X, Escape,
 * wheel-down, swipe-up, or the scroll hint.
 *
 * Critical (matches React):
 * - Stop Lenis while open so wheel dismiss does NOT scroll the homepage under the overlay
 * - On dismiss, force scroll to top, then restart Lenis
 */
(function () {
  var SEEN_KEY = 'necklace-takeover-seen';
  var DISMISS_DISTANCE_PX = 80;
  var DISMISS_VELOCITY_PX_PER_MS = 0.45;
  var COMMIT_DRAG_PX = 12;

  var root = document.querySelector('[data-ll-necklace-takeover]');
  if (!root) return;

  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* ignore */
    }
  }

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  if (readSession(SEEN_KEY) === '1') {
    document.documentElement.classList.remove('ll-necklace-takeover-pending');
    root.remove();
    return;
  }

  var phase = 'open';
  var dragY = 0;
  var isDragging = false;
  var bodyOverflowPrev = '';
  var touchStartY = null;
  var touchStartTime = 0;
  var lastTouchY = null;
  var lastTouchTime = 0;
  var gestureCommitted = false;

  writeSession(SEEN_KEY, '1');
  document.documentElement.classList.add('ll-necklace-takeover-active');
  document.documentElement.classList.remove('ll-necklace-takeover-pending');
  bodyOverflowPrev = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  document.body.classList.add('overflow-hidden');

  /* React: lenis.stop() while takeover is open — otherwise wheel scrolls homepage underneath */
  if (window.lenis && typeof window.lenis.stop === 'function') {
    window.lenis.stop();
  }

  root.hidden = false;
  root.setAttribute('aria-hidden', 'false');

  function scrollHomeToTop() {
    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch (e) {}
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    var lenis = window.lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(0, { immediate: true });
      if (typeof lenis.scroll !== 'undefined') lenis.scroll = 0;
    }
  }

  function applyTransform() {
    var translate =
      phase === 'closing'
        ? 'translateY(-100%)'
        : dragY > 0
          ? 'translateY(-' + dragY + 'px)'
          : 'translateY(0)';
    var useTransition = phase === 'closing' || !isDragging;
    root.style.transform = translate;
    root.style.transition = useTransition
      ? prefersReducedMotion()
        ? 'none'
        : 'transform 700ms ease-in-out'
      : 'none';
  }

  function unlockBody() {
    document.body.style.overflow = bodyOverflowPrev || '';
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('ll-necklace-takeover-active');
    /* React: after dismiss, homepage starts at top — not mid-scroll from Lenis */
    scrollHomeToTop();
    requestAnimationFrame(scrollHomeToTop);
    if (window.lenis && typeof window.lenis.start === 'function') {
      window.lenis.start();
    }
  }

  function dismiss() {
    if (phase !== 'open') return;
    isDragging = false;
    dragY = 0;
    if (prefersReducedMotion()) {
      phase = 'closed';
      unlockBody();
      root.remove();
      return;
    }
    phase = 'closing';
    applyTransform();
    window.setTimeout(function () {
      phase = 'closed';
      unlockBody();
      root.remove();
    }, 750);
  }

  function onWheel(event) {
    if (phase !== 'open') return;
    if (event.deltaY > 5) dismiss();
  }

  function onKeyDown(event) {
    if (phase !== 'open') return;
    if (event.key === 'Escape') dismiss();
  }

  function resetTouchGesture() {
    touchStartY = null;
    lastTouchY = null;
    gestureCommitted = false;
    isDragging = false;
    dragY = 0;
    applyTransform();
  }

  function onTouchStart(event) {
    if (phase !== 'open' || prefersReducedMotion()) return;
    var y = event.touches[0] && event.touches[0].clientY;
    if (y === undefined) return;
    var now = performance.now();
    touchStartY = y;
    touchStartTime = now;
    lastTouchY = y;
    lastTouchTime = now;
    gestureCommitted = false;
    isDragging = true;
    dragY = 0;
    applyTransform();
  }

  function onTouchMove(event) {
    if (phase !== 'open' || prefersReducedMotion()) return;
    if (touchStartY === null) return;
    var current = event.touches[0] && event.touches[0].clientY;
    if (current === undefined) return;

    var upward = touchStartY - current;
    if (upward > COMMIT_DRAG_PX) {
      gestureCommitted = true;
      if (event.cancelable) event.preventDefault();
    }
    if (!gestureCommitted) return;

    dragY = Math.max(0, upward);
    lastTouchY = current;
    lastTouchTime = performance.now();
    applyTransform();
  }

  function onTouchEnd() {
    if (phase !== 'open') {
      resetTouchGesture();
      return;
    }

    if (prefersReducedMotion()) {
      if (touchStartY !== null && lastTouchY !== null && touchStartY - lastTouchY > 30) {
        dismiss();
      } else {
        resetTouchGesture();
      }
      return;
    }

    var distance = dragY;
    var elapsed = Math.max(1, lastTouchTime - touchStartTime);
    var velocity = distance / elapsed;

    if (
      gestureCommitted &&
      (distance >= DISMISS_DISTANCE_PX || velocity >= DISMISS_VELOCITY_PX_PER_MS)
    ) {
      dismiss();
      return;
    }

    isDragging = false;
    touchStartY = null;
    lastTouchY = null;
    gestureCommitted = false;
    requestAnimationFrame(function () {
      dragY = 0;
      applyTransform();
    });
  }

  /* Capture phase like React — see wheel before Lenis */
  window.addEventListener('wheel', onWheel, { capture: true, passive: true });
  window.addEventListener('keydown', onKeyDown);
  root.addEventListener('touchstart', onTouchStart, { passive: true });
  root.addEventListener('touchmove', onTouchMove, { passive: false });
  root.addEventListener('touchend', onTouchEnd, { passive: true });
  root.addEventListener('touchcancel', onTouchEnd, { passive: true });

  root.querySelectorAll('[data-ll-takeover-dismiss]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      dismiss();
    });
  });

  applyTransform();
})();
